'use client';

import { ColorAlternative } from '@coolblue-development/next-patterns/client';
import {
  AccessorySlot,
  AlternativeProduct,
  HighlightedAlternative,
  Product
} from '@features/add-to-cart-page/typings/Product';
import React, { useEffect } from 'react';
import { useState } from 'react';

type ContextApi = {
  // State
  selectedProductAlternative: number;
  setSelectedProductAlternative: (id: number) => void;
  selectedColorAlternatives: Record<number, number>;
  selectedProductQuantity: number;
  setSelectedProductQuantity: (quantity: number, orderQuantityLimit?: number | null) => void;
  updateSelectedColorAlternatives: (id: number, index: number) => void;
  // Data
  initialProductId: number;
  productAlternatives: Record<number, AlternativeProduct>;
  updateProductAlternatives: ({
    accessorySlot,
    highlightedAlternative
  }: {
    accessorySlot?: AccessorySlot;
    highlightedAlternative?: HighlightedAlternative;
  }) => void;
  // Utils
  sanitizeColorAlternativesData: (product: Product) => ColorAlternative[];
};

const AccessoryProductCardContext = React.createContext<ContextApi | null>(null);

export function AccessoryProductCardProvider({
  children,
  initialProduct,
  initialQuantity = 1
}: {
  children: React.ReactNode;
  initialProduct: Product;
  initialQuantity?: number;
}) {
  const initialProductId = initialProduct.id;
  const [selectedProductAlternative, setSelectedProductAlternative] =
    useState<number>(initialProductId);

  const [selectedColorAlternatives, setSelectedColorAlternatives] = useState<
    Record<number, number>
  >({ [initialProductId]: 0 });

  const [productAlternatives, setProductAlternatives] = useState<
    Record<number, AlternativeProduct>
  >(() => {
    const baseAlternatives = { [initialProductId]: initialProduct };

    initialProduct.colorAlternatives?.forEach(colorAlternative => {
      if (!colorAlternative.product) return;
      baseAlternatives[colorAlternative.product.id] = {
        ...colorAlternative.product
      };
    });

    return baseAlternatives;
  });

  function getQuantity({ limit, quantity }: { limit?: number | null; quantity: number }) {
    return limit && limit < quantity ? limit : quantity;
  }

  const [selectedProductQuantity, setSelectedProductQuantity] = useState(
    getQuantity({ limit: initialProduct.orderQuantityLimit, quantity: initialQuantity })
  );

  useEffect(() => {
    if (!productAlternatives[selectedProductAlternative]) return;

    const validatedQuantity = getQuantity({
      limit: productAlternatives[selectedProductAlternative].orderQuantityLimit,
      quantity: selectedProductQuantity
    });

    if (validatedQuantity !== selectedProductQuantity) {
      setSelectedProductQuantity(validatedQuantity);
    }
  }, [productAlternatives, selectedProductAlternative, selectedProductQuantity]);

  function updateSelectedColorAlternatives(id: number, index: number) {
    setSelectedColorAlternatives({ ...selectedColorAlternatives, [id]: index });
  }

  function updateProductAlternatives({
    accessorySlot,
    highlightedAlternative
  }: {
    accessorySlot?: AccessorySlot;
    highlightedAlternative?: HighlightedAlternative;
  }) {
    const updatedAlternatives = { ...productAlternatives };

    // Highlighted alternatives
    (accessorySlot?.highlightedAlternative || highlightedAlternative)?.slots.forEach(slot => {
      const slotProduct = slot.products[0].product;

      if (slotProduct === null) {
        return;
      }

      updatedAlternatives[slotProduct.id] = {
        ...slotProduct,
        isHighlighted: true,
        slotTitle: slot.title
      };

      // and included color alternatives
      slotProduct.colorAlternatives?.forEach(colorAlternative => {
        if (!colorAlternative.product) return;
        updatedAlternatives[colorAlternative.product.id] = {
          ...colorAlternative.product,
          isHighlighted: true,
          slotTitle: slot.title
        };
      });

      // Initial color picker position for slots
      if (
        slotProduct.colorAlternatives &&
        selectedColorAlternatives[slotProduct.id] === undefined
      ) {
        updateSelectedColorAlternatives(slotProduct.id, 0);
      }
    });

    // Non highlighted alternatives
    accessorySlot?.products.forEach(slot => {
      const slotProduct = slot.product;
      updatedAlternatives[slotProduct.id] = { ...slotProduct };
    });

    setProductAlternatives({ ...updatedAlternatives });
  }

  function sanitizeColorAlternativesData(product: Product) {
    if (!product.colorAlternatives) return [];

    const sanitizedData: ColorAlternative[] = [];

    product.colorAlternatives.forEach(alternative => {
      if (alternative?.product?.id && alternative.product?.primaryImage?.id) {
        sanitizedData.push({
          color: alternative.color,
          productId: alternative.product.id,
          imageId: alternative.product.primaryImage.id
        });
      }
    });

    return sanitizedData;
  }

  function handleQuantityChange(quantity: number, orderQuantityLimit?: number | null) {
    const validatedQuantity = getQuantity({ limit: orderQuantityLimit, quantity });
    setSelectedProductQuantity(validatedQuantity);
  }

  return (
    <AccessoryProductCardContext.Provider
      value={{
        selectedProductAlternative,
        setSelectedProductAlternative,
        selectedColorAlternatives,
        selectedProductQuantity,
        setSelectedProductQuantity: handleQuantityChange,
        updateSelectedColorAlternatives,
        initialProductId,
        productAlternatives,
        updateProductAlternatives,
        sanitizeColorAlternativesData
      }}
    >
      {children}
    </AccessoryProductCardContext.Provider>
  );
}

export const useAccessoryProductCardContext = () => {
  const context = React.useContext(AccessoryProductCardContext);

  if (!context) {
    throw new Error(
      'useAccessoryProductCardContext must be used within a AccessoryProductCardContext.Provider'
    );
  }

  return context;
};
